/** Schritt 4 „Kandidatenliste“: Filter, Tabelle der finalen Kandidatenliste, Drawer „Talent-Vorschau“. */

import { saveAs } from 'file-saver';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WizInfoBar, WizLink } from '../components/wizard-ui';
import { listShowsSearch } from '../search-gate.logic';
import { useWizardCandidates } from '../use-wizard-candidates';
import AnalysisLimitDialog from './candidate-list/analysis-limit-dialog';
import CandidateDrawer from './candidate-list/candidate-drawer';
import SexDialog from './candidate-list/sex-dialog';
import {
  applyTableFilters,
  candidatesCsv,
  dailyRunDone,
  EMPTY_FILTERS,
  limitPopupKey,
  markLimitPopupShown,
  pruneSelection,
  showSearchRecommendation,
  TableFilters,
  toggleAll,
  wasLimitPopupShown,
} from './candidate-list/candidate-list.logic';
import CandidateTable from './candidate-list/candidate-table';
import type { WizardStepProps } from './step.types';
import type { WizardCandidate } from '../campaign-wizard.types';

const StepCandidateList: React.FC<WizardStepProps> = ({ wizard }) => {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<string | null>(null);
  const [sexCandidate, setSexCandidate] = useState<WizardCandidate | null>(null);
  const [filters, setFilters] = useState<TableFilters>(EMPTY_FILTERS);
  const [selectedRaw, setSelected] = useState<string[]>([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMessage, setBulkMessage] = useState<string | null>(null);
  const candidates = useWizardCandidates({
    campaignId: wizard.campaignId,
    state: wizard.state,
    patchState: wizard.patch,
    initialStartFailure: wizard.searchFailure,
    onStartFailure: wizard.setSearchFailure,
  });

  // Die Liste kennt die Suche sofort (auch nach „Suche erneut starten“): dem Assistenten melden, damit er vor Änderungen fragt.
  const searchShown = listShowsSearch(candidates.list);
  const { markSearchExists } = wizard;
  useEffect(() => {
    if (searchShown) markSearchExists();
  }, [searchShown, markSearchExists]);

  // Alles, was „alle“ meint (Auswahl, Neubewerten, Blacklist, Export, Blättern im Drawer), wirkt auf die gefilterten Zeilen.
  const visible = useMemo(
    () => applyTableFilters(candidates.list.items, filters),
    [candidates.list.items, filters]
  );
  const selected = useMemo(() => pruneSelection(selectedRaw, visible), [selectedRaw, visible]);
  const selectedItems = useMemo(
    () => visible.filter(c => selected.includes(c.id)),
    [visible, selected]
  );

  const close = useCallback(() => setOpenId(null), []);

  // Info-Popup „Tageslimit erreicht“: einmal pro Kampagne und Tag, wenn alles Mögliche für heute analysiert ist.
  const [limitPopup, setLimitPopup] = useState(false);
  const runDone = dailyRunDone(candidates.list);
  useEffect(() => {
    if (!runDone || !wizard.campaignId) return;
    const key = limitPopupKey(wizard.campaignId, Date.now());
    if (wasLimitPopupShown(key)) return;
    markLimitPopupShown(key);
    setLimitPopup(true);
  }, [runDone, wizard.campaignId]);

  const runBulk = useCallback(
    async (run: (c: (typeof selectedItems)[number]) => Promise<boolean>) => {
      setBulkBusy(true);
      setBulkMessage(null);
      let done = 0;
      for (const item of selectedItems) {
        if (await run(item)) done += 1;
      }
      setBulkMessage(t('wizListBulkDone', { done, total: selectedItems.length }));
      setBulkBusy(false);
    },
    [selectedItems, t]
  );

  const bulk = {
    busy: bulkBusy,
    message: bulkMessage,
    rejudge: () =>
      void runBulk(c =>
        c.ratingState === 'pending' || c.ratingState === 'analysing'
          ? Promise.resolve(false)
          : candidates.rejudge(c)
      ),
    blacklist: (scope: 'person' | 'company') => {
      if (!window.confirm(t('wizListBulkConfirmBlacklist', { count: selectedItems.length })))
        return;
      void runBulk(c => candidates.addToBlacklist(c, scope, 'campaign'));
    },
    exportCsv: () => {
      const rows = selectedItems.length > 0 ? selectedItems : visible;
      saveAs(new Blob([candidatesCsv(rows)], { type: 'text/csv;charset=utf-8' }), 'kandidaten.csv');
    },
  };

  return (
    <>
      {showSearchRecommendation(candidates.list) ? (
        <div role="status" data-testid="wiz-search-recommendation">
          <WizInfoBar>
            <span>
              {t('wizListRecommend')}{' '}
              <WizLink onClick={() => void wizard.next()}>{t('wizListForecastContinue')}</WizLink>
            </span>
          </WizInfoBar>
        </div>
      ) : null}
      <CandidateTable
        candidates={candidates}
        estimate={wizard.estimate.value}
        searchMessage={wizard.searchMessage}
        openId={openId}
        onOpen={setOpenId}
        filters={filters}
        onFilters={setFilters}
        visible={visible}
        selected={selected}
        onSelected={setSelected}
        onSelectAll={() => setSelected(toggleAll(selected, visible))}
        bulk={bulk}
        onSexClick={setSexCandidate}
        onFixLinks={() => wizard.goTo(2)}
      />
      <SexDialog candidate={sexCandidate} onClose={() => setSexCandidate(null)} onSave={candidates.setSex} />
      <AnalysisLimitDialog
        open={limitPopup}
        limit={candidates.list.analysisForecast?.dailyLimit ?? 0}
        onClose={() => setLimitPopup(false)}
      />
      {openId && wizard.campaignId ? (
        <CandidateDrawer
          campaignId={wizard.campaignId}
          candidateId={openId}
          items={visible}
          state={wizard.state}
          blacklists={candidates.blacklists}
          onNavigate={setOpenId}
          onClose={close}
          actionError={candidates.actionError}
          onSetActive={candidates.setActive}
          onSetJudgement={candidates.setJudgementOf}
          onRejudge={candidates.rejudge}
          onAddToBlacklist={candidates.addToBlacklist}
          onRemoveFromBlacklist={candidates.removeFromBlacklist}
        />
      ) : null}
    </>
  );
};

export default StepCandidateList;
